import {
  Select,
  Button,
  Popover,
  Card,
  DatePicker,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useState, useEffect } from "react";

const { Text } = Typography;

type PeriodType = "daily" | "weekly" | "monthly" | "yearly";

interface ChartFiltersProps {
  periodDefault?: PeriodType;
  onChange?: (data: {
    period: PeriodType;
    fromDate: string;
    toDate: string;
  }) => void;
  showDateFilter?: boolean;
  showPeriodFilter?: boolean;
}

const ChartFilters = ({
  periodDefault = "monthly",
  onChange,
  showDateFilter = false,
  showPeriodFilter = false,
}: ChartFiltersProps) => {

  const [period, setPeriod] = useState<PeriodType>(periodDefault);
  const [open, setOpen] = useState(false);
  const [baseDate, setBaseDate] = useState<Dayjs>(dayjs());
  const [fromDate, setFromDate] = useState<Dayjs>();
  const [toDate, setToDate] = useState<Dayjs>();


  const calculateRange = (type: PeriodType, base: Dayjs) => {
    let from: Dayjs;
    let to: Dayjs = base; // ALWAYS end at selected date

    switch (type) {
      case "daily":
        from = base.subtract(6, "day"); // last 7 days
        break;

      case "weekly":
        from = base.subtract(3, "week"); // last 4 weeks
        break;

      case "monthly":
        from = base.subtract(5, "month"); // last 6 months
        break;

      case "yearly":
        from = base.subtract(5, "year"); // last 6 years
        break;

      default:
        from = base;
    }

    return {
      from: from.startOf("day"),
      to: to.endOf("day"),
    };
  }

  const updateRange = (type: PeriodType, base: Dayjs) => {
    const range = calculateRange(type, base);

    setFromDate(range.from);
    setToDate(range.to);

    onChange?.({
      period: type,
      fromDate: range.from.format("YYYY-MM-DD"),
      toDate: range.to.format("YYYY-MM-DD"),
    });
  };


  useEffect(() => {
    updateRange(period, baseDate);
  }, []);


  const handlePeriodChange = (value: PeriodType) => {
    setPeriod(value);
    updateRange(value, baseDate);
  };

  const handleDateChange = (date: Dayjs | null) => {
    const selected = date || dayjs();
    setBaseDate(selected);
    updateRange(period, selected);
    setOpen(false);
  };

  const formatDisplay = (date?: Dayjs) => {
    if (!date) return "";
    return date.format("MMM DD, YYYY");
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center w-full">

      {/* Spacer pushes filter to right */}
      <div className="flex-1" />

      {/* Right Side - Date + Filter */}
      <div className="flex flex-wrap items-center gap-3 justify-end">

        {/* Date Range Display */}
        {showDateFilter && (
          <div className="text-xs sm:text-sm font-semibold whitespace-nowrap">
            {formatDisplay(fromDate)} - {formatDisplay(toDate)}
          </div>
        )}

        {/* Period Select */}
        {showPeriodFilter && (
          <Select
            value={period}
            onChange={handlePeriodChange}
            size="middle"
            className="min-w-[110px] sm:w-[130px]"
          >
            <Select.Option value="daily">Daily</Select.Option>
            <Select.Option value="weekly">Weekly</Select.Option>
            <Select.Option value="monthly">Monthly</Select.Option>
            <Select.Option value="yearly">Yearly</Select.Option>
          </Select>
        )}

        {/* Customize Button */}
        {showDateFilter && (
          <Popover
            trigger="click"
            open={open}
            onOpenChange={setOpen}
            placement="bottomRight"
            content={
              <Card className="w-72">
                <div className="flex flex-col gap-3">
                  <div>
                    <Text className="text-xs font-semibold">
                      Select Date
                    </Text>

                    <DatePicker
                      className="w-full mt-1"
                      value={baseDate}
                      onChange={handleDateChange}
                    />

                    <Text type="secondary" className="text-xs mt-1 block">
                      {period === "daily" && "Previous 7 days"}
                      {period === "weekly" && "Previous 4 weeks"}
                      {period === "monthly" && "Previous 6 months"}
                      {period === "yearly" && "Previous 6 years"}
                    </Text>
                  </div>

                  <Button block onClick={() => handleDateChange(dayjs())}>
                    Reset to Today
                  </Button>
                </div>
              </Card>
            }
          >
            <Button className="sm:w-[120px]">
              Customize
            </Button>
          </Popover>
        )}
      </div>
    </div>
  );


};

export default ChartFilters;
